import { useEffect, useId, useRef, type KeyboardEvent } from "react";
import { Check, MessageCircle } from "lucide-react";

import type { ActivityHostRenderContext } from "@/features/activity-host";
import {
  useCurriculumFeedbackAudio,
  useCurriculumVoiceSequence,
  type CurriculumAudioPlayer
} from "@/features/audio";
import { CurriculumVoiceButton, PrimaryCommandButton } from "@/ui";

import { UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER } from "./focus-social-audio";
import type {
  FocusSocialFriendshipStoryAction,
  FocusSocialFriendshipStoryRenderModel
} from "./friendship-story-adapter";
import "./focus-social-player.css";

export type FocusSocialFriendshipStoryRendererProps = ActivityHostRenderContext<
  FocusSocialFriendshipStoryAction,
  FocusSocialFriendshipStoryRenderModel
> & {
  readonly audioPlayer?: CurriculumAudioPlayer;
};

export function FocusSocialFriendshipStoryRenderer({
  audioPlayer = UNAVAILABLE_FOCUS_SOCIAL_AUDIO_PLAYER,
  getActivationProps,
  model,
  reducedMotion
}: FocusSocialFriendshipStoryRendererProps) {
  const titleId = useId();
  const choiceRefs = useRef(new Map<string, HTMLButtonElement>());
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const followUp = model.followUp;
  const feedbackPlayback =
    model.feedback?.audioPath === null || model.feedback === null
      ? null
      : {
          audioPath: model.feedback.audioPath,
          playbackRequest: model.feedback.playbackRequest
        };
  const followUpPlayback =
    followUp?.audioPath === null || followUp === null
      ? null
      : {
          audioPath: followUp.audioPath,
          playbackRequest: followUp.playbackRequest
        };
  const feedbackAudio = useCurriculumFeedbackAudio(
    audioPlayer,
    feedbackPlayback,
    false
  );
  useCurriculumVoiceSequence(audioPlayer, [feedbackPlayback, followUpPlayback]);

  useEffect(() => {
    if (model.status === "reviewing") {
      reviewHeadingRef.current?.focus();
    }
  }, [model.status]);

  function focusAdjacentChoice(
    event: KeyboardEvent<HTMLButtonElement>,
    choiceId: string,
    offset: -1 | 1
  ) {
    const choiceIds = model.choices.map(({ id }) => id);
    const currentIndex = choiceIds.indexOf(choiceId);

    if (choiceIds.length === 0 || currentIndex === -1) {
      return;
    }

    event.preventDefault();

    const nextIndex =
      (currentIndex + offset + choiceIds.length) % choiceIds.length;
    const nextId = choiceIds[nextIndex];

    if (nextId !== undefined) {
      choiceRefs.current.get(nextId)?.focus();
    }
  }

  function handleChoiceKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    choiceId: string
  ) {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        focusAdjacentChoice(event, choiceId, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        focusAdjacentChoice(event, choiceId, -1);
        break;
    }
  }

  if (model.status !== "choosing") {
    return (
      <section
        aria-labelledby={titleId}
        className="focus-social-player focus-social-story focus-social-story--review"
        data-reduced-motion={reducedMotion}
      >
        <MessageCircle
          aria-hidden="true"
          className="focus-social-player__complete-icon"
        />
        <h2 id={titleId} ref={reviewHeadingRef} tabIndex={-1}>
          这个办法想清楚啦
        </h2>
        <div className="focus-social-player__review-copy">
          {model.feedback !== null ? (
            <div className="focus-social-player__review-voice-row">
              <p
                aria-live="polite"
                className="focus-social-player__story-response"
                data-feedback-kind={model.feedback.kind}
                role="status"
              >
                {model.feedback.message}
              </p>
              {model.feedback.audioPath === null ? null : (
                <CurriculumVoiceButton
                  isPlaying={feedbackAudio.isPlaying}
                  label={model.feedback.message}
                  onPlay={feedbackAudio.replay}
                />
              )}
            </div>
          ) : null}
          {followUp !== null ? (
            <div className="focus-social-player__review-voice-row">
              <p className="focus-social-player__follow-up">{followUp.text}</p>
              {followUp.audioPath === null ? null : (
                <CurriculumVoiceButton
                  isPlaying={audioPlayer.isPlayingPath(followUp.audioPath)}
                  label={followUp.text}
                  onPlay={() => {
                    if (followUp.audioPath !== null) {
                      void audioPlayer.playPath(followUp.audioPath);
                    }
                  }}
                />
              )}
            </div>
          ) : null}
        </div>
        {model.status === "reviewing" ? (
          <PrimaryCommandButton
            {...getActivationProps(() => ({ type: "continue" }))}
            icon={Check}
          >
            我知道了
          </PrimaryCommandButton>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-labelledby={titleId}
      className="focus-social-player focus-social-story"
      data-reduced-motion={reducedMotion}
    >
      <div className="focus-social-player__story-scene">
        <MessageCircle aria-hidden="true" />
        <p>{model.narration.text}</p>
        {model.narration.audioPath === null ? null : (
          <CurriculumVoiceButton
            isPlaying={audioPlayer.isPlayingPath(model.narration.audioPath)}
            label={model.narration.text}
            onPlay={() => {
              if (model.narration.audioPath !== null) {
                void audioPlayer.playPath(model.narration.audioPath);
              }
            }}
          />
        )}
      </div>
      <h2 id={titleId}>你会怎么做？</h2>

      <div
        aria-label="可以选择的做法"
        className="focus-social-player__story-choices"
        role="group"
      >
        {model.choices.map((choice) => {
          const activationProps = getActivationProps((inputMethod) => ({
            type: "choose",
            choiceId: choice.id,
            inputMethod
          }));

          return (
            <div
              className="focus-social-player__story-choice-wrap"
              key={choice.id}
            >
              <button
                {...activationProps}
                aria-pressed={choice.isSelected}
                className="focus-social-player__story-choice"
                data-selected={choice.isSelected}
                onKeyDown={(event) => handleChoiceKeyDown(event, choice.id)}
                ref={(node) => {
                  if (node === null) {
                    choiceRefs.current.delete(choice.id);
                  } else {
                    choiceRefs.current.set(choice.id, node);
                  }
                }}
                type="button"
              >
                <MessageCircle aria-hidden="true" />
                <span>{choice.label}</span>
              </button>
              {choice.audioPath === null ? null : (
                <CurriculumVoiceButton
                  isPlaying={audioPlayer.isPlayingPath(choice.audioPath)}
                  label={choice.label}
                  onPlay={() => {
                    if (choice.audioPath !== null) {
                      void audioPlayer.playPath(choice.audioPath);
                    }
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="focus-social-player__feedback-row">
        <p
          aria-live="polite"
          className="focus-social-player__feedback"
          data-feedback-kind={model.feedback?.kind ?? "none"}
          role="status"
        >
          {model.feedback?.message ?? "先听听每个想法，再选最贴心的一个。"}
        </p>
        {model.feedback?.audioPath === null ||
        model.feedback === null ? null : (
          <CurriculumVoiceButton
            isPlaying={feedbackAudio.isPlaying}
            label={model.feedback.message}
            onPlay={feedbackAudio.replay}
          />
        )}
      </div>
    </section>
  );
}
